/** Formation field for the desktop watchlist Pitch view. */
import React, { memo, useCallback, useMemo, useState } from "react";
import type { LayoutChangeEvent } from "react-native";
import { resolvePitchCardTops } from "@/components/atoms/PitchSurface/layout";

import { Box } from "@/components/atoms/Box";
import { Dot } from "@/components/atoms/Dot";
import { PitchSurface } from "@/components/atoms/PitchSurface";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import PositionNode from "@/features/scouting/components/molecules/PitchPositionNode";
import {
  PITCH_CARD_COMPACT_LINE_CAP,
  PITCH_CARD_LINE_CAP,
} from "@/features/scouting/components/molecules/PitchPositionNode/PitchPositionNodeView";
import type { PitchMemberRemoveState } from "@/features/scouting/components/molecules/PitchPositionPlayerLine";
import { PITCH_FORMATION_ROWS } from "@/features/scouting/constants/pitchFormationRows";
import { PITCH_433_ANCHORS } from "@/features/scouting/constants/pitchFormationAnchors";
import { PitchFormation } from "@/generated/graphql";
import type {
  ScoutingGetWatchlistPitchQuery,
  TacticalSlot,
} from "@/generated/graphql";
import { groupPitchMembers } from "@/features/scouting/utils/watchlistPitchPresentation";
import type { SquadOverlayProjection } from "@/features/scouting/utils/watchlistPitchOverlay";

/** Authorized Pitch query payload. */
type Pitch = NonNullable<ScoutingGetWatchlistPitchQuery["getWatchlistPitch"]>;
/** One authorized watchlist member. */
type Member = Pitch["members"]["edges"][number]["node"];

/** Closed top-inset presets of the field. */
export type PitchFieldTopInset = "sm" | "md";

/** Props for the Pitch formation field. */
interface WatchlistPitchFieldContainerProps {
  readonly members: readonly Member[];
  readonly formation: Pitch["formation"];
  /** Read-only current-squad players per slot; empty when the overlay is off. */
  readonly squadBySlot?: SquadOverlayProjection["squadBySlot"];
  readonly canAssign?: boolean;
  readonly onAssign?: (playerId: string, slot: TacticalSlot | null) => void;
  /** Opens the ranked pop-out for one slot (SE-8314). */
  readonly onOpenGroup?: (slot: TacticalSlot) => void;
  /**
   * Removes a member from the scenario (SE-8353). Rendered only when
   * provided, so the watchlist Pitch is unchanged.
   */
  readonly onRemoveMember?: (playerId: string) => void;
  /** Where each member's removal stands (SE-8353); idle when absent. */
  readonly removeStateById?: Readonly<Record<string, PitchMemberRemoveState>>;
  /**
   * Space above the surface (SE-8486): the watchlist frame sets it 9 px
   * below the column top (281:2911, W42 → `sm` = 8), the scenario frame
   * 17 px (255:6987, S05 → `md` = 16).
   */
  readonly topInset?: PitchFieldTopInset;
  /**
   * SE-8486 (W77/S40): what sits directly under the surface and BEFORE the
   * legend row — the Unassigned panel in the frames (971:19828 / 971:18999),
   * 0 gap to the surface, inset 24 like the legend. Absent = nothing.
   */
  readonly belowSurface?: React.ReactNode;
  /** The three weakest slots: their cards draw a dashed border (SE-8526). */
  readonly weakestSlots?: ReadonlySet<TacticalSlot>;
  /**
   * The visible height of the column the field sits in (SE-8526). The whole
   * surface is scaled to fit it, leaving room to see that content follows;
   * absent/null = the 570 px reference width.
   */
  readonly fitHeight?: number | null;
}

const TOP_INSET: Record<PitchFieldTopInset, string> = {
  sm: "pt-2",
  md: "pt-4",
};

/** The px each top-inset class resolves to (keep in step with TOP_INSET). */
const TOP_INSET_PX: Record<PitchFieldTopInset, number> = { sm: 8, md: 16 };

/**
 * What stays visible under a fitted surface: the 16 px gap (`gap-4`) plus a
 * 24 px peek of whatever follows, so the reader sees there is more below
 * without scrolling the pitch out of view (SE-8526).
 */
const FIT_RESERVE_PX = 40;

/**
 * SE-8535: the narrowest surface a fit may shrink to. The widest formation
 * row holds four 118 px cards (472 px); with at least a few px between and
 * around them that is 500 px. The old floor was a 480 px HEIGHT, i.e. a
 * 394 px width, where four-card rows always collided (Raniel, 2026-09-24).
 * Below the floor the column scrolls, as it already does.
 */
const MIN_FIT_SURFACE_WIDTH_PX = 500;

/** The frame's reference field width; below it the cards turn compact. */
const REFERENCE_FIELD_WIDTH_PX = 570;

/**
 * The surface's 1 px border on each side: the measure View fills the surface
 * inside it, so the 570 px reference measures 568 (SE-8535).
 */
const SURFACE_BORDER_PX = 2;

/**
 * The surface width that makes the whole 570×694 field fit a column height.
 * @param fitHeight - The column's visible height
 * @param topInset - The surface's top inset preset
 * @returns The width cap in px, or undefined when nothing is measured yet.
 */
const fitSurfaceWidth = (
  fitHeight: number | null | undefined,
  topInset: PitchFieldTopInset
): number | undefined => {
  if (!fitHeight) return undefined;
  const surfaceHeight = fitHeight - TOP_INSET_PX[topInset] - FIT_RESERVE_PX;
  const width = Math.floor((surfaceHeight * 570) / 694);
  // Never past the 570 px reference: an inline max-width overrides the
  // surface's own `max-w-pitch-surface`, so a tall column would otherwise
  // stretch the field wider than the frame.
  return Math.min(
    REFERENCE_FIELD_WIDTH_PX,
    Math.max(MIN_FIT_SURFACE_WIDTH_PX, width)
  );
};

/**
 * Renders formation markings and each populated tactical slot.
 * @param root0 - Field presentation props.
 * @param root0.members - Ordered watchlist members.
 * @param root0.formation - Formation used to place tactical slots.
 * @param root0.squadBySlot - Current-squad players drawn per slot.
 * @param root0.canAssign - Whether formation nodes accept assignment drops.
 * @param root0.onAssign - Handles field player assignment requests.
 * @param root0.onOpenGroup - Opens the ranked pop-out for one slot.
 * @param root0.onRemoveMember - Removes a member from the scenario, when offered.
 * @param root0.removeStateById - Per-member removal state (removing / failed / conflict) for the chips.
 * @param root0.topInset - Space above the surface; the watchlist's 8 by default.
 * @param root0.belowSurface - Content between the surface and the legend (SE-8486).
 * @param root0.weakestSlots - The three weakest slots (dashed cards).
 * @param root0.fitHeight - The column's visible height, to fit the surface in.
 */
const WatchlistPitchFieldContainer = ({
  members,
  formation,
  squadBySlot,
  canAssign = false,
  onAssign,
  onOpenGroup,
  onRemoveMember,
  removeStateById,
  topInset = "sm",
  belowSurface,
  weakestSlots,
  fitHeight,
}: WatchlistPitchFieldContainerProps) => {
  const [fieldWidth, setFieldWidth] = useState(570);
  const [sizes, setSizes] = useState<
    Readonly<Record<string, { width: number; height: number }>>
  >({});
  const measureField = useCallback((event: LayoutChangeEvent) => {
    if (event.nativeEvent.layout.width > 0)
      setFieldWidth(event.nativeEvent.layout.width);
  }, []);
  const measureCards = useMemo(
    () =>
      Object.fromEntries(
        PITCH_433_ANCHORS.map(({ slot }) => [
          slot,
          (event: LayoutChangeEvent) => {
            const { width, height } = event.nativeEvent.layout;
            setSizes(previous =>
              previous[slot]?.width === width &&
              previous[slot]?.height === height
                ? previous
                : { ...previous, [slot]: { width, height } }
            );
          },
        ])
      ),
    []
  );
  const scale = fieldWidth / 570;
  const tops = resolvePitchCardTops(
    PITCH_433_ANCHORS.map(({ slot, x, y }) => ({
      id: slot,
      x: x * scale,
      y: y * scale,
      width: sizes[slot]?.width ?? 0,
      height: sizes[slot]?.height ?? 0,
    })),
    694 * scale
  );
  const grouped = groupPitchMembers(members);
  const assignmentSlots = (PITCH_FORMATION_ROWS[formation] ?? []).flat();
  const renderPosition = (slot: TacticalSlot) => (
    <PositionNode
      key={slot}
      slot={slot}
      players={grouped[slot] ?? []}
      squadPlayers={squadBySlot?.[slot]}
      canAssign={canAssign}
      assignmentSlots={assignmentSlots}
      onAssign={onAssign}
      onOpenGroup={onOpenGroup}
      onRemoveMember={onRemoveMember}
      removeStateById={removeStateById}
      isWeakest={weakestSlots?.has(slot) ?? false}
      lineCap={
        fieldWidth < REFERENCE_FIELD_WIDTH_PX - SURFACE_BORDER_PX
          ? PITCH_CARD_COMPACT_LINE_CAP
          : PITCH_CARD_LINE_CAP
      }
    />
  );
  const positions =
    formation === PitchFormation.F_433
      ? PITCH_433_ANCHORS.map(({ slot, x, y }) => ({
          id: slot,
          x,
          y: (tops[slot] ?? y * scale) / scale,
          onLayout: measureCards[slot],
          content: renderPosition(slot),
        }))
      : undefined;
  return (
    // `w-full`: the pitch column centres its children, which shrink-wraps this
    // wrapper — without it the field's width came from the legend row below
    // (300 px on a 1920 px screen, SE-8442) instead of reaching the surface's
    // 570 px. The top inset is the frame's per surface (ties round down).
    <Box
      testID="watchlist-pitch-field"
      className={`w-full items-center ${TOP_INSET[topInset]}`}
    >
      <PitchSurface
        onLayout={measureField}
        maxWidth={fitSurfaceWidth(fitHeight, topInset)}
        testID="watchlist-pitch-surface"
        accessibilityLabel="Watchlist formation pitch"
        positions={positions}
      >
        {!positions ? (
          <Stack className="absolute inset-0 z-10 justify-around py-10">
            {(PITCH_FORMATION_ROWS[formation] ?? []).map((row, index) => (
              <Stack
                key={`pitch-row-${index}`}
                direction="horizontal"
                className="items-center justify-around"
              >
                {row.map(renderPosition)}
              </Stack>
            ))}
          </Stack>
        ) : null}
      </PitchSurface>
      {/* SE-8526 (Raniel, 2026-09-23): 16 px between the surface and what
          follows it — the hint, the Unassigned panel, the legend. The frames
          drew them flush (W77); flush read as cramped once the hint shipped. */}
      <Stack testID="watchlist-pitch-under-surface" className="w-full pt-4">
        {belowSurface ? (
          <Box
            testID="watchlist-pitch-below-surface"
            className="w-full items-center px-6"
          >
            {belowSurface}
          </Box>
        ) : null}
        {/* SE-8486 (W44/S07): a 22 px legend row (micro text + 4 above and
          below), left-aligned 24 px in from the column edge — the frame
          aligns it to the column, not to the centred surface. */}
        <Stack
          direction="horizontal"
          testID="watchlist-pitch-legend"
          className="w-full items-center justify-start gap-4 px-6 py-1"
        >
          <Stack direction="horizontal" className="items-center gap-1">
            {/* SE-8526: the legend uses the card's markers — ○ target. */}
            <Dot
              testID="watchlist-pitch-legend-target"
              className="border border-content-subtle bg-transparent"
            />
            <Text variant="micro" className="text-content-muted">
              Target
            </Text>
          </Stack>
          {squadBySlot ? (
            <Stack direction="horizontal" className="items-center gap-1">
              <Dot
                testID="watchlist-pitch-legend-squad"
                className="bg-accent-primary"
              />
              <Text variant="micro" className="text-content-muted">
                Squad
              </Text>
            </Stack>
          ) : null}
          <Text variant="micro" className="text-content-muted">
            Slots are calculated unless previously saved
          </Text>
        </Stack>
      </Stack>
    </Box>
  );
};

WatchlistPitchFieldContainer.displayName = "WatchlistPitchFieldContainer";
export default memo(WatchlistPitchFieldContainer);
